import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useMasterEvent } from '../../context/MasterEventContext';
import { useParams, useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth';
import { scoringService } from '../../services/scoring';
import { teamsService } from '../../services/teams';
import { masterEventService } from '../../services/masterEvent';
import { feedService } from '../../services/feed';
import { useAuth } from '../../context/AuthContext';
import ActivityForm from '../shared/ActivityForm';
import { activityDisplayName, isActivityHidden, isActivityObfuscated } from '../../services/activityVisibility';
import Avatar from '../shared/Avatar';
import TeamTag from '../shared/TeamTag';
import './MasterEventHub.css';

const TYPE_ICONS = {
  teamselection: '👥',
  betting: '🎯',
  openbets: '🤝',
  food: '🍽️',
  game: '🎮',
  quiz: '🧠',
  vote: '🗳️',
  transport: '🚀',
  accommodation: '🏨',
  custom: '📋',
};

const TRANSPORT_ICONS = {
  train: '🚆', flight: '✈️', car: '🚗', boat: '⛵',
  bus: '🚌', taxi: '🚕', walk: '🚶', bike: '🚲',
};

const ACCOMMODATION_ICONS = {
  hotel: '🏨', hostel: '🛏️', airbnb: '🏠', camping: '⛺', resort: '🏖️', cabin: '🏡',
};

const STATUS_CLASSES = {
  active: 'hub-status--active',
  concluded: 'hub-status--concluded',
  upcoming: 'hub-status--upcoming',
  draft: 'hub-status--draft',
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return null; }
}

// ===== Timeline Comment Popup =====
function TimelineCommentPopup({ activityId, masterEventId, onClose }) {
  const { user } = useAuth();
  const allUsers = authService.getAllUsers();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentError, setCommentError] = useState('');

  const loadComments = useCallback(async () => {
    setComments(await feedService.getForActivity(activityId, 20));
  }, [activityId]);

  useEffect(() => { loadComments(); }, [loadComments]);

  const handlePost = async () => {
    if (!newComment.trim() || !user) return;
    setCommentError('');
    try {
      await feedService.post({ masterEventId, activityId, type: 'comment', userId: user.id, content: newComment.trim() });
      setNewComment('');
      await loadComments();
    } catch (err) {
      setCommentError(err.message || 'Could not post comment');
    }
  };

  return (
    <div className="htl-comment-popup" onClick={(e) => e.stopPropagation()}>
      <div className="htl-comment-popup-header">
        <span style={{ fontWeight: 700, fontSize: 13 }}>Comments</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 14 }}>✕</button>
      </div>
      {commentError && (
        <div style={{ color: '#f87171', fontSize: 11, padding: '0 0 6px' }}>{commentError}</div>
      )}
      <div className="htl-comment-popup-list">
        {comments.length === 0 ? (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', padding: 8 }}>No comments yet</div>
        ) : (
          comments.map((item) => {
            const cu = allUsers.find((u) => u.id === item.userId);
            return (
              <div key={item.id} className="htl-comment-item">
                <Avatar user={cu} size={16} style={{ flexShrink: 0 }} />
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.3 }}>
                  <strong style={{ color: '#fff' }}>{cu?.displayName || 'Unknown'}</strong><TeamTag user={cu} /> {item.content}
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginTop: 2 }}>
                    {new Date(item.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, padding: '8px 0 0' }}>
        <input type="text" value={newComment} onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePost(); } }}
          placeholder="Add a comment..."
          style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '6px 8px', color: '#fff', fontSize: 12, outline: 'none' }} />
        <button onClick={handlePost} disabled={!newComment.trim()}
          style={{ background: '#22c55e', border: 'none', borderRadius: 6, padding: '6px 12px', color: '#fff', fontWeight: 700, fontSize: 11, cursor: 'pointer', opacity: newComment.trim() ? 1 : 0.5 }}>
          Post
        </button>
      </div>
    </div>
  );
}

// ===== Helper: get the display icon for an activity =====
function getActivityIcon(act) {
  // If the activity has a custom image (emoji), use that
  if (act.image && !act.image.startsWith('data:')) return act.image;
  // For transport, use the transport mode icon
  if (act.type === 'transport') return TRANSPORT_ICONS[act.transportMode] || '🚀';
  // For accommodation, use the accommodation type icon
  if (act.type === 'accommodation') return ACCOMMODATION_ICONS[act.accommodationType] || '🏨';
  // Fall back to type icon
  return TYPE_ICONS[act.type] || '📋';
}

// ===== Format a compact date+time string for the brick lane =====
function formatBrickDateTime(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

// ===== Multi-Lane Horizontal Timeline =====
// Layout, top to bottom:
//   - Activity blocks (lane A high / lane B low, alternating by index):
//       each block stacks [name → icon → date], whole block moves together
//       dotted vline drops from below the date to the infra lane
//   - Infra lane: accommodation + transport rectangles share one lane,
//       each rectangle shows [name] / [date/time] on two lines
function HorizontalTimeline({ activities, meId, navigate, masterEvent, masterEventId, canSeeObfuscatedDetails }) {
  const scrollRef = useRef(null);
  const [openCommentId, setOpenCommentId] = useState(null);
  const [commentCounts, setCommentCounts] = useState({});
  const now = Date.now();

  useEffect(() => {
    let cancelled = false;
    async function loadCommentCounts() {
      const entries = await Promise.all(activities.map(async (act) => {
        const comments = await feedService.getForActivity(act.id, 100);
        return [act.id, comments.length];
      }));
      if (!cancelled) setCommentCounts(Object.fromEntries(entries));
    }
    loadCommentCounts();
    return () => { cancelled = true; };
  }, [activities]);

  const sorted = [...activities].sort((a, b) => {
    if (a.date && b.date) return new Date(a.date) - new Date(b.date);
    if (a.date) return -1;
    if (b.date) return 1;
    return a.order - b.order;
  });

  const isInfra = (a) => a.type === 'accommodation' || a.type === 'transport';
  const infraItems = sorted.filter(isInfra);
  const labelItems = sorted.filter((a) => !isInfra(a));

  // Slot-based positioning so items don't bunch up by raw time
  const slotMap = {};
  sorted.forEach((act, idx) => { slotMap[act.id] = idx; });
  const totalSlots = sorted.length;
  const slotPercent = (act) => {
    if (totalSlots <= 1) return 50;
    return (slotMap[act.id] / (totalSlots - 1)) * 100;
  };

  const allDates = sorted.filter((a) => a.date).map((a) => new Date(a.date).getTime());
  const minTime = allDates.length ? Math.min(...allDates) : now;
  const maxTime = allDates.length ? Math.max(...allDates) : now;
  const timeSpan = maxTime - minTime || 1;
  let nowPercent = 0;
  if (allDates.length === 0 || now <= minTime) nowPercent = 0;
  else if (now >= maxTime) nowPercent = 100;
  else nowPercent = ((now - minTime) / timeSpan) * 100;

  // Each infra rectangle spans from its slot to the next infra slot (or end of timeline)
  const infraSpans = infraItems.map((item, idx) => {
    const startPct = slotPercent(item);
    const endPct = idx < infraItems.length - 1 ? slotPercent(infraItems[idx + 1]) : 100;
    return { ...item, startPct, widthPct: Math.max(endPct - startPct, 3) };
  });

  const SLOT_WIDTH = 110;
  const trackWidth = Math.max(totalSlots * SLOT_WIDTH, 400);

  return (
    <div className="htl-wrapper">
      <div className="htl-scroll" ref={scrollRef}>
        <div className="htl-stack" style={{ width: trackWidth, position: 'relative' }}>
          {/* NOW needle — full height vertical line */}
          <div className="htl-needle" style={{ left: `${nowPercent}%` }}>
            <div className="htl-needle-head">NOW</div>
            <div className="htl-needle-line" />
          </div>

          {/* === Activity blocks (lane A / lane B) === */}
          <div className="htl-blocks-container">
            {labelItems.map((act, idx) => {
              const lane = idx % 2 === 0 ? 'a' : 'b';
              const leftPct = slotPercent(act);
              const count = commentCounts[act.id] || 0;
              const isHidden = isActivityHidden(act, masterEvent, canSeeObfuscatedDetails);
              const isObfuscated = isActivityObfuscated(act, masterEvent);
              const hint = isObfuscated && act.hint?.trim();
              return (
                <div
                  key={act.id}
                  className={`htl-block htl-block--${lane}${isObfuscated ? ' htl-block--obfuscated' : ''}`}
                  style={{ left: `${leftPct}%` }}
                  onClick={() => { if (!(isHidden && act.type === 'betting')) navigate(`/me/${meId}/activity/${act.id}`); }}
                  aria-disabled={isHidden && act.type === 'betting' ? true : undefined}
                >
                  <div className="htl-block-name" title={isHidden ? undefined : act.name}>{isHidden ? 'Hidden activity' : act.name}</div>
                  {hint && <div className="htl-block-hint" title={hint}>💡 {hint}</div>}
                  <div className="htl-block-icon-wrap">
                    <div className={`htl-dot htl-dot--${act.status}${act.status === 'draft' ? ' htl-dot--is-draft' : ''}`}>
                      <span className="htl-dot-icon">{isHidden ? '🙈' : getActivityIcon(act)}</span>
                    </div>
                    {isObfuscated && !isHidden && <span className="htl-obfuscated-badge" title="Obfuscated for members">🙈</span>}
                    <button
                      className="htl-comment-badge"
                      onClick={(e) => { e.stopPropagation(); setOpenCommentId(openCommentId === act.id ? null : act.id); }}
                      title="Comments"
                    >
                      <span>💬</span>
                      {count > 0 && <span className="htl-comment-count">{count}</span>}
                    </button>
                  </div>
                  {act.date && <div className="htl-block-date">{isHidden ? 'Hidden' : formatBrickDateTime(act.date)}</div>}
                  <div className="htl-block-vline" />
                  {openCommentId === act.id && (
                    <TimelineCommentPopup activityId={act.id} masterEventId={masterEventId} onClose={() => setOpenCommentId(null)} />
                  )}
                </div>
              );
            })}
          </div>

          {/* === Combined infra lane (accommodation + transport rectangles) === */}
          {infraSpans.length > 0 && (
            <div className="htl-bar-lane">
              <div className="htl-bar-track" />
              {infraSpans.map((item) => {
                const count = commentCounts[item.id] || 0;
                const isHidden = isActivityHidden(item, masterEvent, canSeeObfuscatedDetails);
                const isObfuscated = isActivityObfuscated(item, masterEvent);
                const hint = isObfuscated && item.hint?.trim();
                return (
                  <div
                    key={item.id}
                    className={`htl-bar-block htl-bar-block--${item.type}${item.status === 'draft' ? ' htl-bar-block--draft' : ''}${isObfuscated ? ' htl-bar-block--obfuscated' : ''}`}
                    style={{ left: `${item.startPct}%`, width: `${item.widthPct}%` }}
                    onClick={() => navigate(`/me/${meId}/activity/${item.id}`)}
                  >
                    <div className="htl-bar-name" title={isHidden ? undefined : item.name}>{isObfuscated && <span title={isHidden ? undefined : 'Obfuscated for members'}>🙈 </span>}{isHidden ? 'Hidden activity' : item.name}</div>
                    {hint && <div className="htl-block-hint" title={hint}>💡 {hint}</div>}
                    {item.date && <div className="htl-bar-date">{isHidden ? 'Hidden' : formatBrickDateTime(item.date)}</div>}
                    <button
                      className="htl-comment-badge htl-comment-badge--bar"
                      onClick={(e) => { e.stopPropagation(); setOpenCommentId(openCommentId === item.id ? null : item.id); }}
                      title="Comments"
                    >
                      <span>💬</span>
                      {count > 0 && <span className="htl-comment-count">{count}</span>}
                    </button>
                    {openCommentId === item.id && (
                      <TimelineCommentPopup activityId={item.id} masterEventId={masterEventId} onClose={() => setOpenCommentId(null)} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== Members Panel =====
function MembersPanel({ members, allUsers, currentUserId }) {
  const [locationInput, setLocationInput] = useState('');
  const [editingUserId, setEditingUserId] = useState(null);

  const handleShareLocation = (userId) => {
    if (!locationInput.trim()) return;
    authService.setLocation(userId, locationInput.trim());
    setLocationInput('');
    setEditingUserId(null);
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="hub-section hub-section--members">
      <div className="hub-section-header">
        <h3>Members</h3>
        <span className="hub-section-count">{members.length}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {members.map((mem) => {
          const memberUser = mem.user || allUsers.find((u) => u.id === mem.userId);
          const loc = memberUser?.location;
          const hasLocation = loc?.link;
          const isMe = mem.userId === currentUserId;
          const isEditing = editingUserId === mem.userId;

          return (
            <div key={mem.id} style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Avatar user={memberUser} size={20} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{memberUser?.displayName || 'Unknown'}<TeamTag user={memberUser} userId={mem.userId} /></div>
                  {hasLocation && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                      <a href={loc.link} target="_blank" rel="noopener noreferrer"
                        style={{ color: '#60a5fa', fontSize: 12, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                        📍 View location
                      </a>
                      {loc.updatedAt && <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>{timeAgo(loc.updatedAt)}</span>}
                    </div>
                  )}
                </div>
                {isMe && !isEditing && (
                  <button onClick={() => { setEditingUserId(mem.userId); setLocationInput(loc?.link || ''); }}
                    style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 6, padding: '4px 10px', color: '#60a5fa', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    {hasLocation ? '📍 Update' : '📍 Share Location'}
                  </button>
                )}
              </div>
              {isEditing && (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, paddingLeft: 30 }}>
                  <input type="text" value={locationInput} onChange={(e) => setLocationInput(e.target.value)}
                    placeholder="Paste Google Maps link..." onKeyDown={(e) => e.key === 'Enter' && handleShareLocation(mem.userId)} autoFocus
                    style={{ flex: 1, padding: '7px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 12, outline: 'none' }} />
                  <button onClick={() => handleShareLocation(mem.userId)} disabled={!locationInput.trim()}
                    style={{ background: '#22c55e', border: 'none', borderRadius: 8, padding: '7px 14px', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: locationInput.trim() ? 1 : 0.5 }}>Save</button>
                  <button onClick={() => { setEditingUserId(null); setLocationInput(''); }}
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '7px 10px', color: 'rgba(255,255,255,0.6)', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===== Main Hub =====
export default function MasterEventHub() {
  const { masterEvent, activities, feed, scores, members, isEventAdmin, isActivityAdmin, loading, refreshData, updateActivityLocal } = useMasterEvent();
  const { user } = useAuth();
  const { id: meId } = useParams();
  const navigate = useNavigate();
  const allUsers = authService.getAllUsers();
  const canSeeObfuscatedDetails = isActivityAdmin || isEventAdmin;
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [editingActId, setEditingActId] = useState(null);
  const [sortBy, setSortBy] = useState('date');
  const [activityError, setActivityError] = useState('');

  if (loading) return <div className="hub-loading">Loading...</div>;
  if (!masterEvent) return <div className="hub-loading">Event not found</div>;

  const recentFeed = (feed || []).slice(0, 5);

  const leaderboard = {};
  (scores || []).forEach((s) => {
    const key = s.userId || s.teamId;
    if (!leaderboard[key]) leaderboard[key] = { userId: s.userId, teamId: s.teamId, totalPoints: 0 };
    leaderboard[key].totalPoints += s.points;
  });
  const topScorers = Object.values(leaderboard).sort((a, b) => b.totalPoints - a.totalPoints).slice(0, 3);

  const sortedActivities = [...activities].sort((a, b) => {
    if (sortBy === 'name') return (activityDisplayName(a, masterEvent, canSeeObfuscatedDetails) || '').localeCompare(activityDisplayName(b, masterEvent, canSeeObfuscatedDetails) || '');
    if (sortBy === 'created') return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    // default: 'date' — items with a date first, ascending; undated last by `order`
    if (a.date && b.date) return new Date(a.date) - new Date(b.date);
    if (a.date) return -1;
    if (b.date) return 1;
    return (a.order || 0) - (b.order || 0);
  });

  return (
    <div className="hub">
      <div className="hub-header">
        <div>
          <h2 className="hub-title">{masterEvent.name}</h2>
          {masterEvent.description && <p className="hub-description">{masterEvent.description}</p>}
          <div className="hub-meta">
            <span className="hub-meta-item">👥 {members.length} members</span>
            <span className="hub-meta-item">📌 {activities.length} activities</span>
            <span className={`hub-status ${STATUS_CLASSES[masterEvent.status] || ''}`}>{masterEvent.status}</span>
          </div>
        </div>
      </div>

      {/* Horizontal Timeline at top */}
      {activities.length > 0 && (
        <HorizontalTimeline
          activities={activities}
          meId={meId}
          navigate={navigate}
          masterEventId={masterEvent.id}
          masterEvent={masterEvent}
          canSeeObfuscatedDetails={canSeeObfuscatedDetails}
        />
      )}

      <div className="hub-grid">
        {/* Activities */}
        <div className="hub-section hub-section--activities">
          <div className="hub-section-header">
            <h3>Activities</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="hub-section-count">{activities.length}</span>
              <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', display: 'flex', alignItems: 'center', gap: 4 }}>
                Sort
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="hub-sort-select"
                >
                  <option value="date">By date (occurs)</option>
                  <option value="created">Newest created</option>
                  <option value="name">Alphabetical</option>
                </select>
              </label>
              {(isEventAdmin || isActivityAdmin) && (
                <button className="hub-quick-add-btn" onClick={() => setShowQuickAdd(!showQuickAdd)}>
                  {showQuickAdd ? '✕' : '+ New'}
                </button>
              )}
            </div>
          </div>
          {activityError && (
            <div style={{
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 8,
              padding: '8px 10px',
              marginBottom: 10,
              color: '#f87171',
              fontSize: 12,
            }}>
              {activityError}
            </div>
          )}
          {showQuickAdd && (
            <div style={{ marginBottom: 14, padding: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
              <ActivityForm
                onSubmit={async (data) => {
                  setActivityError('');
                  try {
                    await masterEventService.createActivity({ masterEventId: meId, ...data });
                    setShowQuickAdd(false);
                    await refreshData();
                  } catch (err) {
                    setActivityError(err.message || 'Could not create activity');
                  }
                }}
                onCancel={() => setShowQuickAdd(false)}
                submitLabel="Create Activity"
              />
            </div>
          )}
          {activities.length === 0 ? (
            <p className="hub-empty">No activities yet. Create one from the Admin panel.</p>
          ) : (
            <div className="hub-activities-grid">
              {sortedActivities.map((act) => {
                const isHidden = isActivityHidden(act, masterEvent, canSeeObfuscatedDetails);
                const displayName = activityDisplayName(act, masterEvent, canSeeObfuscatedDetails);
                if (editingActId === act.id) {
                  return (
                    <div key={act.id} className="hub-activity-card" style={{ border: '1px solid rgba(59,130,246,0.25)' }} onClick={(e) => e.stopPropagation()}>
                      <ActivityForm
                        initial={act}
                        masterEvent={masterEvent}
                        onSubmit={async (data) => {
                          setActivityError('');
                          try {
                            await updateActivityLocal(act.id, data);
                            setEditingActId(null);
                          } catch (err) {
                            setActivityError(err.message || 'Could not save activity');
                          }
                        }}
                        onCancel={() => setEditingActId(null)}
                        submitLabel="Save"
                        compact
                      />
                    </div>
                  );
                }
                return (
                  <div key={act.id} className={`hub-activity-card${act.status === 'draft' ? ' hub-activity-card--draft' : ''}`} aria-disabled={isHidden && act.type === 'betting' ? true : undefined} onClick={() => { if (!(isHidden && act.type === 'betting')) navigate(`/me/${meId}/activity/${act.id}`); }}>
                    <div className="hub-activity-card-top">
                      <span className="hub-activity-icon">{isHidden ? '🙈' : getActivityIcon(act)}</span>
                      <span className={`hub-status hub-status--small ${STATUS_CLASSES[act.status] || ''}`}>{act.status}</span>
                      {isHidden && <span style={{ fontSize: 14 }} title="Details hidden">🙈</span>}
                      {canSeeObfuscatedDetails && (
                        <button
                          className="hub-activity-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            const next = act.status === 'draft' ? 'active' : 'draft';
                            setActivityError('');
                            updateActivityLocal(act.id, { status: next })
                              .catch((err) => setActivityError(err.message || 'Could not update activity status'));
                          }}
                          title={act.status === 'draft' ? 'Publish (go live)' : 'Move back to draft'}
                          style={{ opacity: 1 }}
                        >
                          {act.status === 'draft' ? '🚀' : '📝'}
                        </button>
                      )}
                      {isEventAdmin && (
                        <div className="hub-activity-actions">
                          <button className="hub-activity-action-btn" onClick={(e) => { e.stopPropagation(); setEditingActId(act.id); }} title="Edit">✏️</button>
                          <button className="hub-activity-action-btn hub-activity-action-btn--danger" onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete "${act.name}"?`)) {
                              setActivityError('');
                              masterEventService.deleteActivity(act.id)
                                .then(() => refreshData())
                                .catch((err) => setActivityError(err.message || 'Could not delete activity'));
                            }
                          }} title="Delete">🗑️</button>
                        </div>
                      )}
                    </div>
                    <h4 className="hub-activity-name">{displayName}</h4>
                    {isHidden ? (
                      <p className="hub-activity-desc" style={{ color: '#fbbf24', fontStyle: 'italic' }}>
                        {act.hint?.trim() || 'Details hidden... stay tuned!'}
                      </p>
                    ) : (
                      <>
                        {act.date && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>{formatDate(act.date)}</div>}
                        {act.description && <p className="hub-activity-desc">{act.description}</p>}
                      </>
                    )}
                    <div className="hub-activity-link">{isHidden && act.type === 'betting' ? 'Awaiting reveal' : 'View →'}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Mini Feed */}
        <div className="hub-section hub-section--feed">
          <div className="hub-section-header">
            <h3>Recent Activity</h3>
            <button className="hub-section-link" onClick={() => navigate(`/me/${meId}/feed`)}>See all →</button>
          </div>
          {recentFeed.length === 0 ? (
            <p className="hub-empty">No feed items yet.</p>
          ) : (
            <div className="hub-feed-list">
              {recentFeed.map((item) => {
                const feedUser = allUsers.find((u) => u.id === item.userId);
                return (
                  <div key={item.id} className="hub-feed-item">
                    <Avatar user={feedUser} size={18} className="hub-feed-avatar" />
                    <div className="hub-feed-content">
                      <strong>{feedUser?.displayName || 'Unknown'}</strong><TeamTag user={feedUser} />{' '}<span>{item.content}</span>
                      <div className="hub-feed-time">{new Date(item.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick Scores */}
        <div className="hub-section hub-section--scores">
          <div className="hub-section-header">
            <h3>Top Scores</h3>
            <button className="hub-section-link" onClick={() => navigate(`/me/${meId}/scores`)}>Full board →</button>
          </div>
          {topScorers.length === 0 ? (
            <p className="hub-empty">No scores recorded yet.</p>
          ) : (
            <div className="hub-scores-list">
              {topScorers.map((entry, idx) => {
                const scoreUser = allUsers.find((u) => u.id === entry.userId);
                const medals = ['🥇', '🥈', '🥉'];
                return (
                  <div key={entry.userId || entry.teamId} className="hub-score-item">
                    <span className="hub-score-medal">{medals[idx] || `#${idx + 1}`}</span>
                    <Avatar user={scoreUser} size={18} className="hub-score-avatar" />
                    <span className="hub-score-name">{scoreUser?.displayName || 'Team'}</span>
                    <span className="hub-score-points">{entry.totalPoints} pts</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Team Standings */}
        <div className="hub-section hub-section--teams">
          <div className="hub-section-header"><h3>Team Standings</h3></div>
          {(() => {
            const meTeams = teamsService.getForMasterEvent(meId);
            const teamLb = scoringService.getTeamLeaderboard(meId);
            if (meTeams.length === 0) return <p className="hub-empty">No teams set up yet</p>;
            const lbMap = {};
            teamLb.forEach((e) => { lbMap[e.teamId] = e; });
            const merged = meTeams.map((t) => ({ ...t, totalPoints: lbMap[t.id]?.totalPoints || 0 })).sort((a, b) => b.totalPoints - a.totalPoints);
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {merged.map((team) => (
                  <div key={team.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 10, borderLeft: `3px solid ${team.color}` }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: team.color, flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{team.name}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#22c55e' }}>{team.totalPoints} pts</span>
                    <div style={{ display: 'flex', gap: 2, marginLeft: 6 }}>
                      {team.members.slice(0, 5).map((uid) => {
                        const u = allUsers.find((usr) => usr.id === uid);
                        return <Avatar key={uid} user={u} size={14} style={{ display: 'inline-block' }} />;
                      })}
                      {team.members.length > 5 && <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>+{team.members.length - 5}</span>}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Members */}
        <MembersPanel members={members} allUsers={allUsers} currentUserId={user?.id} />
      </div>

    </div>
  );
}
